/**
 * UI State Store using Zustand
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

interface Notification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message?: string;
  duration?: number;
}

interface UIState {
  // Sidebar
  sidebarOpen: boolean;
  sidebarCollapsed: boolean;

  // Theme
  darkMode: boolean;

  // Notifications
  notifications: Notification[];

  // Modal
  modalOpen: boolean;
  modalContent: React.ReactNode | null;
  modalTitle: string;

  // Loading
  globalLoading: boolean;

  // Keyboard shortcuts
  shortcutsEnabled: boolean;

  // Selected items for bulk actions
  selectedItems: string[];

  // Actions
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  toggleSidebarCollapsed: () => void;
  toggleDarkMode: () => void;
  setDarkMode: (dark: boolean) => void;
  addNotification: (notification: Omit<Notification, 'id'>) => void;
  removeNotification: (id: string) => void;
  clearNotifications: () => void;
  openModal: (title: string, content: React.ReactNode) => void;
  closeModal: () => void;
  setGlobalLoading: (loading: boolean) => void;
  toggleShortcuts: () => void;
  setSelectedItems: (items: string[]) => void;
  addSelectedItem: (id: string) => void;
  removeSelectedItem: (id: string) => void;
  clearSelectedItems: () => void;
  toggleSelectedItem: (id: string) => void;
}

export const useUIStore = create<UIState>()(
  persist(
    (set, get) => ({
      // Initial state
      sidebarOpen: true,
      sidebarCollapsed: false,
      darkMode: false,
      notifications: [],
      modalOpen: false,
      modalContent: null,
      modalTitle: '',
      globalLoading: false,
      shortcutsEnabled: true,
      selectedItems: [],

      // Actions
      toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),
      setSidebarOpen: (open) => set({ sidebarOpen: open }),
      toggleSidebarCollapsed: () => set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),
      toggleDarkMode: () => set((state) => ({ darkMode: !state.darkMode })),
      setDarkMode: (dark) => set({ darkMode: dark }),

      addNotification: (notification) => {
        const id = `notification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const newNotification = { ...notification, id };

        set((state) => ({
          notifications: [...state.notifications, newNotification],
        }));

        // Auto-remove after duration
        const duration = notification.duration ?? 5000;
        if (duration > 0) {
          setTimeout(() => {
            get().removeNotification(id);
          }, duration);
        }
      },

      removeNotification: (id) =>
        set((state) => ({
          notifications: state.notifications.filter((n) => n.id !== id),
        })),

      clearNotifications: () => set({ notifications: [] }),

      openModal: (title, content) =>
        set({ modalOpen: true, modalTitle: title, modalContent: content }),

      closeModal: () => set({ modalOpen: false, modalTitle: '', modalContent: null }),

      setGlobalLoading: (loading) => set({ globalLoading: loading }),

      toggleShortcuts: () => set((state) => ({ shortcutsEnabled: !state.shortcutsEnabled })),

      setSelectedItems: (items) => set({ selectedItems: items }),

      addSelectedItem: (id) =>
        set((state) => ({
          selectedItems: state.selectedItems.includes(id)
            ? state.selectedItems
            : [...state.selectedItems, id],
        })),

      removeSelectedItem: (id) =>
        set((state) => ({
          selectedItems: state.selectedItems.filter((item) => item !== id),
        })),

      clearSelectedItems: () => set({ selectedItems: [] }),

      toggleSelectedItem: (id) =>
        set((state) => ({
          selectedItems: state.selectedItems.includes(id)
            ? state.selectedItems.filter((item) => item !== id)
            : [...state.selectedItems, id],
        })),
    }),
    {
      name: 'ui-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        sidebarCollapsed: state.sidebarCollapsed,
        darkMode: state.darkMode,
        shortcutsEnabled: state.shortcutsEnabled,
      }),
    },
  ),
);

// Notification helpers
export const notify = {
  success: (title: string, message?: string) =>
    useUIStore.getState().addNotification({ type: 'success', title, message }),
  error: (title: string, message?: string) =>
    useUIStore.getState().addNotification({ type: 'error', title, message }),
  warning: (title: string, message?: string) =>
    useUIStore.getState().addNotification({ type: 'warning', title, message }),
  info: (title: string, message?: string) =>
    useUIStore.getState().addNotification({ type: 'info', title, message }),
};
